import { HoverLink } from '@campfire/hover-link';
import { Typography, Box, Grid, IconButton, Button, Card, CardContent, Theme } from '@material-ui/core';
import { Event, Add, MoreVertOutlined, PersonAdd, Schedule, Delete } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { memo, useState } from 'react';
import { TooltipMenu } from '../../../../common/tooltip-menu';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { ActivityFilters } from '../../../../global/activity-filters/ActivityFilters';
import { ACTIVITIES_FILTERS_LIST } from '../../../../global/activity-filters/activity-filters-activity.gql';
import {
  GetAllActivitiesForVolunteer,
  GetAllActivitiesForVolunteerVariables,
} from '../../../../global/activity-filters/__generated__/GetAllActivitiesForVolunteer';
import { useUser } from '../../../../global/auth/useUser';
import {
  useApproveIncomingVolunteerFetch,
  useWaitlistIncomingVolunteerFetch,
  useRejectIncomingVolunteerFetch,
} from '../../activity-management/activity-form-v2/activity-form-actions-v2';
import { VolunteerCommonProfileActivityApplication } from '../__generated__/VolunteerCommonProfileActivityApplication';
import { useVolunteerCommonProfileContext } from '../VolunteerCommonProfileContext';

export const VolunteerActivityApplicationsSection = memo(
  ({ activityApplications }: { activityApplications: VolunteerCommonProfileActivityApplication[] }) => {
    const {
      volunteerNameOptions: { preferredName },
      volunteer,
      reloadNewData,
    } = useVolunteerCommonProfileContext();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const apiUrl = useApiUrl();
    const { setSnackbar } = useSnackbar();
    const { run } = useCampfireFetch({ defer: true });
    const { getManagerIdentity, user } = useUser();
    const { programManagerIdentities } = getManagerIdentity();
    const managedProgramIds = programManagerIdentities.flatMap((p) => p.programId);

    const programIds = volunteer?.programs.map((a) => a.programId) ?? [];
    const filteredProgramIds = user.userIdentityService.isProgramManager
      ? programIds.filter((p) => managedProgramIds.includes(p))
      : programIds;

    const { data } = useCampfireQuery<GetAllActivitiesForVolunteer, GetAllActivitiesForVolunteerVariables>(
      ACTIVITIES_FILTERS_LIST,
      {
        options: {
          variables: {
            programIds: filteredProgramIds,
          },
        },
      }
    );

    const setSelectedActivities = async (activityList: string[]) => {
      if (!volunteer?.volunteerId) return;

      try {
        const response = await run({
          method: 'post',
          data: {
            items: [
              {
                volunteerId: volunteer.volunteerId,
                activityIds: activityList,
              },
            ],
          },
          url: `${apiUrl}/vm/activity/application/manager-save`,
        });

        if (response.ok) {
          setSnackbar({
            open: true,
            message: `Successfully created ${activityList.length} application${activityList.length > 1 ? 's' : ''}`,
            variant: 'success',
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Some applications could not be created',
            variant: 'error',
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to create applications',
          variant: 'error',
        });
      } finally {
        reloadNewData();
      }
    };

    const allActivities = data?.vm.programs.flatMap((a) => a.activities);
    const appliedActivities = activityApplications.flatMap((a) => a.activity);

    const activitiesList =
      appliedActivities.length === 0
        ? allActivities
        : allActivities?.filter((a) => !appliedActivities.some((b) => b.activityId === a.activityId));
    return (
      <Grid container spacing={2} direction='column' style={{ position: 'relative' }}>
        <Grid item xs={12} style={{ textAlign: 'left' }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box>
              <Typography
                style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}
              >
                <Event style={{ fontSize: '14px', paddingRight: '5px' }} /> Activity Applications
              </Typography>
              <Typography variant='body2' style={{ color: '#464646' }} align='left'>
                Activities that {preferredName} has applied for
              </Typography>
            </Box>
            <Box>
              <TabletButton
                size='small'
                variant='contained'
                color='primary'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => setDialogOpen(true)}
                startIcon={<Add />}
                style={{
                  border: '1px solid transparent',
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  marginTop: '2px',
                  color: '#fff',
                }}
              >
                Add
              </TabletButton>
            </Box>
          </Box>
          <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />
          {activityApplications
            .filter((activityApplication) => activityApplication.activityApplicationId !== null)
            .map((activityApplication) => (
              <ApplicationCard
                key={activityApplication.activityApplicationId}
                application={activityApplication}
                preferredName={preferredName}
                reloadData={reloadNewData}
              />
            ))}
          {activityApplications.length === 0 && (
            <Typography
              variant='body2'
              style={{ fontSize: '13px', color: '#494949', textAlign: 'center', padding: '2rem 0' }}
            >
              No results found
            </Typography>
          )}
        </Grid>
        {dialogOpen && activitiesList && (
          <ActivityFilters
            activities={activitiesList}
            selectedActivities={[]}
            setSelectedActivities={setSelectedActivities}
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onCancel={() => setDialogOpen(false)}
            fullScreen={false}
            simple
            addVol
          />
        )}
      </Grid>
    );
  }
);

const useCardStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      marginBottom: '12px',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'box-shadow 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      },
    },
    cardContent: {
      padding: '16px !important',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    activityInfo: {
      flex: 1,
    },
    activityName: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '4px',
    },
    programName: {
      fontSize: '12px',
      color: '#666',
    },
    menuItem: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontSize: '14px',
      fontWeight: 500,
      textTransform: 'none',
      width: '100%',
      padding: '6px 8px',
      '& span': { fontSize: '14px' },
      '& svg': { fontSize: '16px' },
    },
    menuItemDestructive: {
      color: theme.alert.red.light,
    },
  })
);

interface ApplicationCardProps {
  application: VolunteerCommonProfileActivityApplication;
  preferredName: string;
  reloadData: () => void;
}

const ApplicationCard: React.FC<ApplicationCardProps> = memo(({ application, preferredName, reloadData }) => {
  const classes = useCardStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const { setSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const approveApplicationFetch = useApproveIncomingVolunteerFetch();
  const waitlistApplicationFetch = useWaitlistIncomingVolunteerFetch();
  const rejectApplicationFetch = useRejectIncomingVolunteerFetch();

  const handleAction = async (action: string) => {
    setMenuOpen(false);
    setLoading(true);

    try {
      switch (action) {
        case 'approve':
          await approveApplicationFetch.run({ activityApplicationId: application.activityApplicationId });
          setSnackbar({
            open: true,
            message: `Successfully added ${preferredName} to ${application.activity.name}`,
            variant: 'success',
          });
          break;
        case 'waitlist':
          await waitlistApplicationFetch.run({ activityApplicationId: application.activityApplicationId });
          setSnackbar({
            open: true,
            message: `Successfully waitlisted ${preferredName} for ${application.activity.name}`,
            variant: 'success',
          });
          break;
        case 'reject':
          await rejectApplicationFetch.run({ activityApplicationId: application.activityApplicationId });
          setSnackbar({
            open: true,
            message: `Successfully rejected application for ${application.activity.name}`,
            variant: 'success',
          });
          break;
        default:
          console.warn(`Unknown action: ${action}`);
          break;
      }
      reloadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Action failed. Please try again.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <HoverLink
          to={`/management/activity-management?activityId=${application.activity.activityId}&programId=${application.activity.program.programId}`}
          hoverColor='primary'
          disableUnderline
          className={classes.activityInfo}
        >
          <Typography className={classes.activityName}>{application.activity.name}</Typography>
          <Typography className={classes.programName}>{application.activity.program.name}</Typography>
        </HoverLink>

        <TooltipMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          title={
            <Box display='flex' flexDirection='column' alignItems='flex-start' style={{ padding: '5px 10px' }}>
              <Button className={classes.menuItem} startIcon={<PersonAdd />} onClick={() => handleAction('approve')}>
                Add to Team
              </Button>
              <Button className={classes.menuItem} startIcon={<Schedule />} onClick={() => handleAction('waitlist')}>
                Move to Waitlist
              </Button>
              <Button
                className={`${classes.menuItem} ${classes.menuItemDestructive}`}
                startIcon={<Delete />}
                onClick={() => handleAction('reject')}
              >
                Reject Application
              </Button>
            </Box>
          }
        >
          <IconButton
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ color: '#0000008A', padding: '0.5rem' }}
            disabled={loading}
          >
            <MoreVertOutlined />
          </IconButton>
        </TooltipMenu>
      </CardContent>
    </Card>
  );
});
